import { useEffect, useRef } from 'react';
import type { PropsWithChildren } from 'react';

import { useEvent, View } from '@tamagui/core';
import { Animated, Easing } from 'react-native';

type HeightAnimatorProps = {
  /**
   * Whether the content is open or not
   */
  isOpen: boolean;
};

/**
 * Tamagui's Accordion.HeightAnimator has some bugs, so this is our own
 * implementation.
 * - Bug #1: For Tamagui Views, height isn't actually animatable on native.
 *   Solution: use Animated.View instead of Tamagui's `animation` prop.
 * - Bug #2: On iOS, onLayout gets called again when the content is still open,
 *   so the height is never recalculated to 0. Solution: check isOpen before
 *   setting the height in the onLayout handler.
 * - Bug #3: The absolute positioned element doesn't take up the full width of
 *   the parent. Solution: set left and right to 0.
 * For Tamagui's Accordion.HeightAnimator implementation, see:
 * https://github.com/tamagui/tamagui/blob/master/packages/accordion/src/Accordion.tsx
 */
export const HeightAnimator = ({ isOpen, children }: PropsWithChildren<HeightAnimatorProps>) => {
  const height = useRef(new Animated.Value(0));

  const setHeight = (toValue: number) => {
    Animated.timing(height.current, {
      toValue,
      // TODO: see if we can use tokens for animation values
      duration: 100,
      easing: Easing.bezier(0.42, 0, 1.0, 1.0),
      useNativeDriver: false, // native driver doesn't support animating height
    }).start();
  };

  useEffect(() => {
    if (!isOpen) {
      setHeight(0);
    }
  }, [isOpen]);

  const onLayout = useEvent(({ nativeEvent }) => {
    if (isOpen && nativeEvent.layout.height) {
      setHeight(nativeEvent.layout.height);
    }
  });

  return (
    <Animated.View style={{ height: height.current }}>
      <View position="absolute" left={0} right={0} onLayout={onLayout}>
        {children}
      </View>
    </Animated.View>
  );
};
